import { useState } from 'react'
import { BaseEdge, getSmoothStepPath, Position } from '@xyflow/react'
import { ROUTE_COLOR, routePath } from './familyRoutes'
import type { FamilyRoute } from './familyRoutes'

/**
 * A connector arrow on the Results Ownership Chart.
 *
 * Each arrow stands for exactly one reporting line, so it is a control rather
 * than decoration: it can be hovered, selected, right-clicked, and its
 * child-end endpoint can be dragged onto another box to redirect the line or
 * into open space to detach it.
 *
 * Two details make that workable:
 *   - a 1.5px line is impossible to hit with a mouse, so a transparent stroke
 *     several times wider carries all the pointer events;
 *   - a viewer gets none of it. `interactive` switches off the hit area, the
 *     hover styling, the handles and the context menu, so the arrow is inert
 *     rather than a control that refuses on click.
 */
export const CONNECTOR_COLOR = ROUTE_COLOR
export const CONNECTOR_SELECTED_COLOR = '#ED1C24'
/** Transparent stroke width that catches the pointer, in flow pixels. */
export const CONNECTOR_HIT_WIDTH = 18

/** How far the in-progress curve leans out of each end, in flow pixels. */
export const DRAG_CURVE_MIN_BOW = 30
export const DRAG_CURVE_MAX_BOW = 160

/**
 * The line drawn while the child end is being pulled around.
 *
 * A routed, right-angled path around a moving end reads as a glitch, and a
 * rigid straight segment reads as a taut wire snapped between two pins. This is
 * a single curve that leaves the parent downwards and arrives at the pointer
 * from above, bowing in proportion to how far the two ends are apart, so the
 * line behaves like something being drawn by hand.
 */
export function dragCurvePath(sourceX: number, sourceY: number, endX: number, endY: number): string {
  const bow = Math.max(
    DRAG_CURVE_MIN_BOW,
    Math.min(DRAG_CURVE_MAX_BOW, Math.abs(endY - sourceY) * 0.6 + Math.abs(endX - sourceX) * 0.2),
  )
  return `M ${sourceX},${sourceY} C ${sourceX},${sourceY + bow} ${endX},${endY - bow} ${endX},${endY}`
}

export interface ConnectorEdgeData {
  bounds?: { sourceX: number; sourceY: number; targetX: number; targetY: number; centerY: number }

  parentSeatId: number

  childSeatId: number

  parentName: string

  childName: string
  /** False for a viewer, and while the Auto Layout preview is on screen. */

  interactive: boolean

  selected: boolean
  /** Where the dragged endpoint currently sits, in flow coordinates. */

  dragPoint: { x: number; y: number } | null

  onSelect: (edgeId: string) => void

  onContextMenu: (payload: {
    edgeId: string
    parentSeatId: number
    childSeatId: number
    clientX: number
    clientY: number

  }) => void

  onEndpointDragStart: (payload: {
    edgeId: string
    parentSeatId: number
    childSeatId: number
    clientX: number
    clientY: number

  }) => void

  onDelete: (childSeatId: number) => void

  route?: FamilyRoute
}

interface ConnectorEdgeProps {
  id: string
  sourceX: number
  sourceY: number
  targetX: number
  targetY: number
  sourcePosition?: Position
  targetPosition?: Position
  markerEnd?: string
  data: ConnectorEdgeData
}

export function ConnectorEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition = Position.Bottom,
  targetPosition = Position.Top,
  markerEnd,
  data,
}: ConnectorEdgeProps) {
  const [hovered, setHovered] = useState(false)
  const {
    parentSeatId, childSeatId, parentName, childName,
    interactive, selected, dragPoint,
    onSelect, onContextMenu, onEndpointDragStart, onDelete,
  } = data

  const dragging = dragPoint !== null
  // React Flow's measured handles can be a frame behind a resize.
  if (data.bounds) {
    sourceX = data.bounds.sourceX
    sourceY = data.bounds.sourceY
    targetX = data.bounds.targetX
    targetY = data.bounds.targetY
  }
  const endX = dragging ? dragPoint.x : targetX
  const endY = dragging ? dragPoint.y : targetY

  const [fallbackPath, fallbackX, fallbackY] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition,
    targetX: endX, targetY: endY, targetPosition,
    centerY: data.bounds?.centerY,
  })
  // While the endpoint is being dragged the arrow follows the pointer as a
  // curve. The loose end itself is eased towards the pointer by the page, so
  // the line reads as being drawn rather than snapped taut.
  const path = dragging ? dragCurvePath(sourceX, sourceY, endX, endY) :
    data.route ? routePath(data.route.branch) : fallbackPath
  const labelX = data.route?.label.x ?? fallbackX
  const labelY = data.route?.label.y ?? fallbackY

  const state = selected ? 'selected' : hovered && interactive ? 'hover' : 'idle'
  const stroke = state === 'idle' ? CONNECTOR_COLOR : CONNECTOR_SELECTED_COLOR
  const strokeWidth = state === 'idle' ? 1.5 : 2.5

  const payload = { edgeId: id, parentSeatId, childSeatId, clientX: 0, clientY: 0 }

  return (
    <>
      {data.route?.shared.map((commands, index) => (
        <path key={index} d={routePath(commands)} fill="none" stroke={CONNECTOR_COLOR}
          strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
          pointerEvents="none" data-testid={`connector-shared-${parentSeatId}-${index}`} />
      ))}
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{
          stroke,
          strokeWidth,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeDasharray: dragging ? '6 4' : undefined,
        }}
        data-testid={`connector-line-${childSeatId}`}
        data-state={state}
      />

      {/* The thing the pointer actually hits. Invisible, and inert for anyone
          who cannot edit the chart. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={CONNECTOR_HIT_WIDTH}
        strokeLinecap="round"
        className="react-flow__edge-interaction"
        data-testid={`connector-hit-${childSeatId}`}
        style={{ pointerEvents: interactive ? 'stroke' : 'none', cursor: interactive ? 'pointer' : 'default' }}
        onMouseEnter={() => interactive && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={event => {
          if (!interactive) return
          event.stopPropagation()
          onSelect(id)
        }}
        onContextMenu={event => {
          if (!interactive) return
          event.preventDefault()
          event.stopPropagation()
          onContextMenu({ ...payload, clientX: event.clientX, clientY: event.clientY })
        }}
      >
        <title>{`${childName} reports to ${parentName}`}</title>
      </path>

      {interactive && selected && (
        <g data-testid={`connector-handles-${childSeatId}`}>
          <g
            data-testid={`connector-delete-${childSeatId}`}
            role="button"
            aria-label={`Delete reporting line from ${childName} to ${parentName}`}
            tabIndex={0}
            transform={`translate(${labelX},${labelY})`}
            style={{ cursor: 'pointer', pointerEvents: 'all' }}
            onPointerDown={event => event.stopPropagation()}
            onClick={event => { event.stopPropagation(); onDelete(childSeatId) }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                onDelete(childSeatId)
              }
            }}
          >
            <circle r={12} fill="#FFFFFF" stroke={CONNECTOR_SELECTED_COLOR} strokeWidth={1.5} />
            <path d="M -4,-4 L 4,4 M 4,-4 L -4,4" stroke={CONNECTOR_SELECTED_COLOR} strokeWidth={2} />
            <title>Delete reporting line</title>
          </g>
          {/* The parent end is shown so the selection reads as a line with two
              ends, but only the child end is draggable: an arrow always points
              at the box whose reporting line it is. */}
          <circle
            cx={sourceX}
            cy={sourceY}
            r={5}
            fill="#FFFFFF"
            stroke={CONNECTOR_SELECTED_COLOR}
            strokeWidth={2}
            data-testid={`connector-source-handle-${childSeatId}`}
            style={{ pointerEvents: 'none' }}
          />
          <circle
            cx={endX}
            cy={endY}
            r={7}
            fill="#FFFFFF"
            stroke={CONNECTOR_SELECTED_COLOR}
            strokeWidth={2.5}
            data-testid={`connector-endpoint-${childSeatId}`}
            style={{ pointerEvents: 'all', cursor: 'grab' }}
            onPointerDown={event => {
              event.preventDefault()
              event.stopPropagation()
              onEndpointDragStart({ ...payload, clientX: event.clientX, clientY: event.clientY })
            }}
            onContextMenu={event => {
              event.preventDefault()
              event.stopPropagation()
              onContextMenu({ ...payload, clientX: event.clientX, clientY: event.clientY })
            }}
          />
        </g>
      )}
    </>
  )
}

export default ConnectorEdge
